/**
 * Done. CPD — Calendar utilities
 * Ported from Jo's calendar.ts with Done. CPD branding.
 */

import type { CpdModule, CalendarEvent } from './types';

const DAY_INDEX: Record<string, number> = {
  Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
  Thursday: 4, Friday: 5, Saturday: 6,
};

const TIME_MAP: Record<string, { hour: number; label: string }> = {
  Morning:   { hour: 9,  label: '9:00 AM'  },
  Midday:    { hour: 12, label: '12:00 PM' },
  Afternoon: { hour: 15, label: '3:00 PM'  },
  Evening:   { hour: 19, label: '7:00 PM'  },
};

export function getEventDate(
  year: number,
  monthIndex: number,
  eventNumber: number,
  preferredDay: string,
  preferredTime: string,
): Date {
  const targetDay = DAY_INDEX[preferredDay] ?? 2;
  const base = new Date(year, monthIndex, eventNumber === 1 ? 1 : 15);
  const offset = (targetDay - base.getDay() + 7) % 7;
  const hour = TIME_MAP[preferredTime]?.hour ?? 9;
  return new Date(year, monthIndex, base.getDate() + offset, hour, 0, 0);
}

export function describePreferredTime(preferredTime: string): string {
  return TIME_MAP[preferredTime]?.label ?? '9:00 AM';
}

function buildCalendarDescription(module: CpdModule): string {
  return [
    `Module title: ${module.title}`,
    `Intended learning outcome: ${module.intendedOutcome}`,
    'Reflection prompt:',
    ...module.reflectionPrompts.map(p => `- ${p}`),
    `Evidence/completion output: ${module.evidenceOutput}`,
    'Reminder: save your certificate or completion evidence and draft your reflection while the learning is fresh.',
  ].join('\n');
}

function toGoogleDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

export function createGoogleCalendarUrl(module: CpdModule, start: Date, end: Date): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `Done. CPD Module: ${module.title}`,
    dates: `${toGoogleDate(start)}/${toGoogleDate(end)}`,
    details: buildCalendarDescription(module),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildVAlarms(title: string, reminders: string[]): string[] {
  const lines: string[] = [];

  // Always add a 1-day-before reminder
  lines.push(
    'BEGIN:VALARM',
    'TRIGGER:-P1D',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeIcsText(`Tomorrow: ${title}`)}`,
    'END:VALARM',
  );

  // Accountability prompts → add a same-morning (1 hour before) reminder too
  if (reminders.includes('Accountability prompts')) {
    lines.push(
      'BEGIN:VALARM',
      'TRIGGER:-PT1H',
      'ACTION:DISPLAY',
      `DESCRIPTION:${escapeIcsText(`Starting soon: ${title}`)}`,
      'END:VALARM',
    );
  }

  return lines;
}

export function createIcsEvent(module: CpdModule, start: Date, end: Date, reminders: string[] = []): string {
  const uid = `${module.id}-${start.toISOString()}@done.cpd`;
  const title = `Done. CPD Module: ${module.title}`;
  const description = buildCalendarDescription(module);
  return [
    'BEGIN:VEVENT',
    `UID:${escapeIcsText(uid)}`,
    `DTSTAMP:${toGoogleDate(new Date())}`,
    `DTSTART:${toGoogleDate(start)}`,
    `DTEND:${toGoogleDate(end)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    ...buildVAlarms(title, reminders),
    'END:VEVENT',
  ].join('\r\n');
}

export function createIcsCalendar(events: CalendarEvent[], reminders: string[] = []): string {
  const eventBlocks = events.map(e => createIcsEvent(e.module, e.start, e.end, reminders));
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Done. CPD//CPD Planner//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...eventBlocks,
    'END:VCALENDAR',
  ].join('\r\n');
}

export function createCalendarEvent(module: CpdModule, start: Date, reminders: string[] = []): CalendarEvent {
  const end = new Date(start.getTime() + module.estimatedMinutes * 60 * 1000);
  return {
    module,
    start,
    end,
    googleUrl: createGoogleCalendarUrl(module, start, end),
    icsContent: createIcsEvent(module, start, end, reminders),
  };
}
